import { FIELD_TYPES, SECTION_COLORS } from './constants';

// ─── Unique ID generator ───────────────────────────────────────────────────────
export function uid() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

// ─── Field type lookup ─────────────────────────────────────────────────────────
export const getFieldTypeInfo = (type) =>
  FIELD_TYPES.find((f) => f.type === type) || FIELD_TYPES[0];

// Localized display name for a field type. Accepts either a type string or a
// FIELD_TYPES entry; falls back to the English label if the key is missing.
export const fieldTypeLabel = (t, typeOrInfo) => {
  const info = typeof typeOrInfo === 'string' ? getFieldTypeInfo(typeOrInfo) : typeOrInfo;
  if (!info) return '';
  return info.labelKey ? t(info.labelKey, info.label) : info.label;
};

// ─── Multilang helpers ─────────────────────────────────────────────────────────
// User-facing schema text is either a plain string or a { lang: text } map.
// These read such a value without caring which shape it is.

// Any non-empty translation? Used by validation, which must not reject a field
// that is filled in Turkish but not English.
export const hasText = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v !== 'object') return false;
  return Object.values(v).some((x) => typeof x === 'string' && x.trim().length > 0);
};

// A representative string for comparison (duplicate detection) and fallbacks.
export const anyText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v !== 'object') return String(v);
  const first = Object.values(v).find((x) => typeof x === 'string' && x.trim());
  return first || '';
};

// Per-language keys, so "Amount/Tutar" and "Amount/Miktar" collide on `en`
// but "Tutar" alone never collides with "Amount" alone.
const textKeys = (v) => {
  if (typeof v === 'string') return v.trim() ? [`*:${v.trim().toLowerCase()}`] : [];
  if (v && typeof v === 'object') {
    return Object.entries(v)
      .filter(([, x]) => typeof x === 'string' && x.trim())
      .map(([lang, x]) => `${lang}:${x.trim().toLowerCase()}`);
  }
  return [];
};

// ─── Factory functions ─────────────────────────────────────────────────────────
export const createField = (type = 'text', overrides = {}) => ({
  id: uid(),
  label: '',
  description: '',
  type,
  required: false,
  placeholder: '',
  options: [],
  width: 'full', // full | half | third | quarter
  order: 0,
  ...overrides,
});

export const createOption = (label = '') => ({ id: uid(), label });

export const createColumn = (overrides = {}) => ({
  id: uid(),
  header: '',
  type: 'text',
  width: 'medium',
  required: false,
  options: [],
  order: 0,
  ...overrides,
});

export const createSection = (type = 'fields', overrides = {}) => ({
  id: uid(),
  title: 'Untitled Section',
  type,
  color: SECTION_COLORS[0],
  collapsed: false,
  fields: type === 'fields' ? [] : undefined,
  tableColumns: type === 'table' ? [] : undefined,
  order: 0,
  ...overrides,
});

// Start with an empty schema so the template picker shows on first load
export const DEFAULT_SCHEMA = () => ({
  sections: [],
});

// ─── Validation ────────────────────────────────────────────────────────────────
export const validateSchema = (schema) => {
  const errors = {};
  schema.sections.forEach((section) => {
    // Passive sections are skipped on the contract form; don't block saving
    // over them.
    if (section.isActive === false) return;
    if (!hasText(section.title)) {
      errors[`section-${section.id}-title`] = 'Section title is required';
    }
    if (section.type === 'fields') {
      const seen = new Set();
      section.fields.forEach((field) => {
        if (field.isActive === false) return;
        if (!hasText(field.label)) {
          errors[`field-${field.id}-label`] = 'Field label is required';
        }
        if (field.type === 'select' && field.options.length === 0) {
          errors[`field-${field.id}-options`] = 'Select field must have at least one option';
        }
        if (field.type === 'radio' && field.options.length === 0) {
          errors[`field-${field.id}-options`] = 'Radio field must have at least one option';
        }
        const keys = textKeys(field.label);
        if (keys.some((k) => seen.has(k))) {
          errors[`field-${field.id}-label`] = 'Duplicate field label in this section';
        }
        keys.forEach((k) => seen.add(k));
      });
    }
    if (section.type === 'table') {
      if (!section.tableColumns || section.tableColumns.length === 0) {
        errors[`section-${section.id}-columns`] = 'Table section must have at least one column';
      }
      const seen = new Set();
      (section.tableColumns || []).forEach((col) => {
        if (!hasText(col.header)) {
          errors[`col-${col.id}-header`] = 'Column header is required';
        }
        if (col.type === 'select' && col.options.length === 0) {
          errors[`col-${col.id}-options`] = 'Select column must have at least one option';
        }
        const keys = textKeys(col.header);
        if (keys.some((k) => seen.has(k))) {
          errors[`col-${col.id}-header`] = 'Duplicate column header in this table';
        }
        keys.forEach((k) => seen.add(k));
      });
    }
  });
  return errors;
};

// ─── Deep clone with fresh IDs ─────────────────────────────────────────────────
export const deepCloneWithFreshIds = (obj) => {
  if (Array.isArray(obj)) return obj.map(deepCloneWithFreshIds);
  if (obj && typeof obj === 'object') {
    const result = {};
    for (const [k, v] of Object.entries(obj)) {
      result[k] = k === 'id' ? uid() : deepCloneWithFreshIds(v);
    }
    return result;
  }
  return obj;
};

// ─── Build save payload ────────────────────────────────────────────────────────
export const buildSavePayload = (name, description, schema) => ({
  name: name.trim(),
  description: description.trim(),
  sections: schema.sections.map((s, si) => {
    const base = {
      id: s.id,
      title: s.title,
      type: s.type,
      color: s.color,
      collapsed: s.collapsed,
      order: si,
      // Standard-template flags: undeletable system sections + passive state.
      isStandard: !!s.isStandard,
      isActive: s.isActive !== false,
    };
    if (s.type === 'fields') {
      return {
        ...base,
        // label / description / placeholder / option labels pass through
        // verbatim — they may be a plain string or a { lang: text } map.
        fields: s.fields.map((f, fi) => ({
          id: f.id,
          label: f.label,
          description: f.description || '',
          type: f.type,
          required: f.required,
          placeholder: f.placeholder,
          width: f.width || 'full',
          order: fi,
          isStandard: !!f.isStandard,
          isActive: f.isActive !== false,
          options: f.options.map((o) => {
            const opt = { id: o.id, label: o.label };
            // Preserve visibilityRule for radio options
            if (f.type === 'radio' && o.visibilityRule) {
              opt.visibilityRule = {
                action: o.visibilityRule.action || 'show',
                fieldIds: o.visibilityRule.fieldIds || [],
              };
            }
            return opt;
          }),
          // Preserve defaultVisibilityRule for radio fields (when nothing is selected)
          ...(f.type === 'radio' && f.defaultVisibilityRule
            ? {
                defaultVisibilityRule: {
                  action: f.defaultVisibilityRule.action || 'show',
                  fieldIds: f.defaultVisibilityRule.fieldIds || [],
                },
              }
            : {}),
        })),
      };
    } else {
      return {
        ...base,
        tableColumns: (s.tableColumns || []).map((c, ci) => ({
          id: c.id,
          header: c.header,
          type: c.type,
          width: c.width,
          required: c.required,
          order: ci,
          options: c.options.map((o) => ({ id: o.id, label: o.label })),
        })),
      };
    }
  }),
});
